/**
 * Касс — борлуулалт бүртгэх. `POST /orders` (нэг транзакц).
 *
 * ⚠️ Сагс нь ЗӨВХӨН клиент дээр. Схемд сагсны хүснэгт байхгүй,
 *    `order_lines` нь INSERT-ээс хойш өөрчлөгдөшгүй тул сервер рүү
 *    НЭГ л удаа бүтнээр илгээнэ (docs/API.md §8).
 *
 * ⚠️ `id` нь сагс бүрд НЭГ удаа үүснэ — сүлжээ тасарч дахин дарвал
 *    сервер ижил `id`-г хараад шинэ захиалга үүсгэхгүй (идемпотент).
 *    Дуудлага тутамд шинэ `id` үүсгэвэл гутал 2 удаа зарагдана.
 *
 * Хэсгүүд: `SaleCart` (скан) · `PaymentPicker` (төлбөр) ·
 * `CustomerFields` (худалдан авагч) · `SaleSuccess` (амжилт). Энэ хуудас
 * тэднийг холбоод захиалгыг илгээнэ.
 *
 * DARK GLASS (2026-10-05): амжилтын мэдэгдэл дээд талын туузаас сагсны
 * ДОТОР шилжив; дүн, нийт тоо гүйж өөрчлөгдөнө; бараатай үед дүнгийн
 * карт гэрэлтэнэ.
 */

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INSTALLMENT_TERM_MAX,
  INSTALLMENT_TERM_MIN,
  VAT_PCT,
  formatDate,
  formatMnt,
  formatSerialDisplay,
  installmentSchedule,
  installmentSplit,
  moneyInput,
  spreadOrderDiscount,
} from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Store } from '../lib/types';
import {
  Button, Card, CardTitle, Chip, ErrorNote, Field, Input, Select, Spinner,
} from '../components/ui';
import { SaleCart, type CartLine } from '../components/sale-cart';
import { PaymentPicker, type PaymentChoice } from '../components/payment-picker';
import { CustomerFields, type CustomerInput } from '../components/customer-fields';
import { SaleSuccess, type OrderCreated } from '../components/sale-success';
import { CountUp } from '../components/count-up';
import { Rise } from '../components/rise';

export function SalesPage() {
  const { user, can } = useAuth();
  const qc = useQueryClient();

  const [lines, setLines] = useState<CartLine[]>([]);
  const [storeId, setStoreId] = useState('');
  const [pay, setPay] = useState<PaymentChoice>({ payments: [], methods: ['CASH'], vatMnt: 0, total: 0 });
  const [customer, setCustomer] = useState<CustomerInput | null>({ marketingConsent: false });
  const [orderDiscount, setOrderDiscount] = useState('');
  const [vat, setVat] = useState(false);
  /** Хувь зээлийн хугацаа — анхдагчгүй, кассчин ЗААВАЛ сонгоно */
  const [term, setTerm] = useState<number | null>(null);
  const [done, setDone] = useState<OrderCreated | null>(null);
  /**
   * Идемпотентлэгийн түлхүүр — сагс бүрд НЭГ. Борлуулалт амжилттай
   * болмогц шинэчлэгдэж, `key`-ээр төлбөр, худалдан авагчийн хэсгийг
   * цэвэрлэнэ (тэдгээр нь дотроо төлөвтэй).
   */
  const [orderId, setOrderId] = useState(() => crypto.randomUUID());
  // Шинэ гутал уншуулмагц өмнөх борлуулалтын амжилтын харагдац хаагдана
  const clearDone = useCallback(() => setDone(null), []);

  const stores = useQuery({
    queryKey: ['ref-stores'],
    queryFn: () => api<Envelope<Store[]>>('/ref/stores'),
    enabled: can('ADMIN'),
  });

  // ADMIN салбараа сонгоно; бусад нь өөрийн салбартаа л зарна
  const needStore = can('ADMIN') && !user?.storeId;
  const effectiveStore = needStore ? storeId : (user?.storeId ?? '');
  const sellingStore = effectiveStore
    ? {
        id: effectiveStore,
        name: needStore
          ? ((stores.data?.data ?? []).find((s) => s.id === effectiveStore)?.name ?? 'Сонгосон салбар')
          : (user?.storeName ?? 'таны салбар'),
      }
    : null;

  const lineTotals = lines.map((l) => Number(l.item.priceMnt) - Number(moneyInput(l.discountMnt)));
  const subtotal = lineTotals.reduce((s, t) => s + t, 0);
  /*
    Нийт дүнгийн хөнгөлөлт — ЗӨВХӨН 2+ гутал (2026-10-11). Захиалгын толгойд
    биш, гутал бүрийн мөрд үнийн харьцаагаар хуваарилагдана: тайлан,
    урамшуулал, нэг гутлын буцаалт мөрийн дүнгээс уншдаг (`spreadOrderDiscount`).
  */
  const multi = lines.length >= 2;
  const cartDiscount = multi ? moneyInput(orderDiscount) : '0';
  const shares = spreadOrderDiscount(lineTotals, cartDiscount);
  const base = Math.max(0, subtotal - Number(cartDiscount));
  /*
    Нэмэгдэл — и-баримт +10%. Дүн нь хуваалтаас хамаарах тул `PaymentPicker`
    бодно (`chargeParts`), сервер ижил функцээр тулгана. Зээлийн апп орсон
    бол и-баримт ЗААВАЛ, зөвхөн аппын хэсэгт (2026-10-06).
  */
  const installment = pay.methods.includes('INSTALLMENT');
  const creditApp = pay.methods.includes('CREDIT_APP');
  const total = pay.total;

  const confirm = useMutation({
    mutationFn: () =>
      api<Envelope<OrderCreated>>('/orders', {
        method: 'POST',
        body: {
          id: orderId,
          ...(effectiveStore ? { storeId: effectiveStore } : {}),
          channel: 'STORE',
          lines: lines.map((l, i) => ({
            itemId: l.item.id,
            // Мөрийн өөрийн хөнгөлөлт + нийт хөнгөлөлтөөс ногдох хувь
            discountMnt: shares![i]!.plus(moneyInput(l.discountMnt)).toFixed(2),
            warrantyDays: l.warrantyDays,
          })),
          ...customer,
          // Хураангуйг сервер `payments`-аас өөрөө бодно
          payment: pay.payments[0]?.method ?? 'CASH',
          payments: pay.payments,
          ...(installment && term ? { installment: { termMonths: term } } : {}),
          // Дүнг биш СОНГОЛТЫГ илгээнэ — нэмэгдлийг сервер бодно
          vat: vat || creditApp,
        },
      }),
    onSuccess: async (res) => {
      setDone(res.data);
      setLines([]);
      setOrderDiscount('');
      setVat(false);
      setTerm(null);
      setOrderId(crypto.randomUUID());
      await qc.invalidateQueries({ queryKey: ['orders'] });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  if (!can('SALESPERSON', 'STORE_MANAGER', 'ADMIN')) {
    return <ErrorNote>Борлуулалт бүртгэх эрх танд байхгүй.</ErrorNote>;
  }

  const blocked =
    lines.length === 0 ||
    shares === null ||
    (needStore && !storeId) ||
    // Төлбөрийн хуваалт дутуу, эсвэл худалдан авагчийн утга буруу.
    // Сервер ямар ч байсан татгалзана.
    pay.payments.length === 0 ||
    customer === null ||
    (installment && !term) ||
    confirm.isPending;

  return (
    /*
      ⚠️ ХОЁР БАГАНА 1280px-ЭЭС ДЭЭШ (2026-09-25). Өмнө нь 1024px-аас
         хуваагддаг байсан: сагс таблет болон жижиг зөөврийн дэлгэц дээр
         шахагдаж байв. 1280px-ээс доош бүх зүйл нэг баганад, бүтэн өргөнөөр.
      ⚠️ Хажуугийн багана ТОГТМОЛ биш — 320–384px хооронд уян:
         1280–1400px дээр сагсанд үлдэх зай хэт багасахгүй.
    */
    <div className="grid gap-3.5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,384px)]">
      <Rise i={1} className="min-w-0">
        <SaleCart
          lines={lines}
          setLines={setLines}
          store={sellingStore}
          onScan={clearDone}
          success={done ? <SaleSuccess order={done} onNew={clearDone} /> : undefined}
        />
      </Rise>

      {/*
        Дүн, «Зарах» товч нь өргөн дэлгэцэд ҮРГЭЛЖ харагдана —
        кассчин сагсаа гүйлгэж байхад нийт дүнгээ хайх шаардлагагүй.
        `Rise` нь key-тэй маягтын ГАДНА — борлуулалт бүрийн дараа картууд
        дахин «үсрэхгүй».
      */}
      <div className="flex flex-col gap-3 xl:sticky xl:top-0 xl:self-start">
        {needStore ? (
          <Rise i={2}>
            <Card className="sm:px-5.5">
              {stores.isPending ? (
                <Spinner label="Салбар…" />
              ) : (
                <Field label="Аль салбарын нэрээр зарах">
                  <Select
                    value={storeId}
                    onChange={setStoreId}
                    placeholder="Сонгох…"
                    options={(stores.data?.data ?? [])
                      .filter((s) => s.type === 'RETAIL')
                      .map((s) => ({ value: s.id, label: s.name }))}
                  />
                </Field>
              )}
            </Card>
          </Rise>
        ) : null}

        {/*
          ⚠️ Түлхүүр нь ЯЛГААТАЙ байх ЁСТОЙ. Хоёр зэрэгцээ элемент нэг
             `key` авбал React тэдгээрийг ялгаж чадахгүй — картууд
             давхарлаж, доорх дүнгийн карт алга болж байв (2026-09-25).
        */}
        <Rise i={3}>
          <PaymentPicker key={`pay-${orderId}`} base={base} vat={vat} onChange={setPay} />
        </Rise>
        {installment ? <InstallmentTerm total={total} term={term} onTerm={setTerm} /> : null}
        <Rise i={4}>
          <CustomerFields key={`customer-${orderId}`} onChange={setCustomer} required={installment} />
        </Rise>

        <Rise i={5}>
          {/* Сагсанд бараа орсон үед гэрэлтэнэ (`surface-glow`) */}
          <Card className={`sm:p-5.5 ${lines.length ? 'surface-glow' : ''}`}>
            <dl className="flex flex-col gap-3.5 text-sm">
              <div className="flex items-baseline justify-between gap-4 text-body">
                <dt className="text-ink-2">Дэд дүн · {lines.length} ширхэг</dt>
                <dd className="font-bold"><CountUp text={formatMnt(subtotal)} duration={800} /></dd>
              </div>
              {multi ? (
                <CartDiscount
                  value={orderDiscount}
                  onChange={setOrderDiscount}
                  lines={lines}
                  shares={shares}
                />
              ) : null}
              {shares === null ? <ErrorNote>Хөнгөлөлт үнээс их байна</ErrorNote> : null}
              {/* Зээлийн апп → заавал: чагтлагдаж түгжигдэнэ, апп хасвал сонголт буцна */}
              <SurchargeToggle
                label={`И-баримт +${VAT_PCT}%`}
                hint={creditApp ? 'Зээлийн апп — заавал, зөвхөн аппын хэсэгт' : 'НӨАТ — баримт авна'}
                checked={vat || creditApp}
                locked={creditApp}
                onChange={setVat}
                amountMnt={String(pay.vatMnt)}
              />
              <div className="mt-1 flex items-baseline justify-between gap-4 border-t border-hair pt-4">
                <dt className="text-h2 font-bold">Нийт</dt>
                <dd className="font-display text-num font-semibold text-accent-700">
                  <CountUp text={formatMnt(total)} duration={800} />
                </dd>
              </div>
            </dl>

            {confirm.error ? (
              <div className="mt-3">
                <ErrorNote>{(confirm.error as Error).message}</ErrorNote>
              </div>
            ) : null}

            {/* Дарахад цагаан дүүргэлт гүйнэ (`sweep`) — серверийн хариу хүлээх зуур */}
            <Button
              onClick={() => confirm.mutate()}
              disabled={blocked}
              className={`relative mt-4 h-13.5 w-full overflow-hidden text-h2 font-bold ${
                confirm.isPending ? 'sweep' : ''
              }`}
            >
              <span className="relative">
                {confirm.isPending ? 'Бүртгэж байна…' : `Зарах · ${formatMnt(total)}`}
              </span>
            </Button>
            <p className="mt-2.5 text-[11.5px] text-ink-3">
              Батлагдсан захиалга өөрчлөгдөшгүй. Алдаатай бол хүчингүй болгож дахин бүртгэнэ.
            </p>
          </Card>
        </Rise>
      </div>
    </div>
  );
}

/**
 * Нийт дүнгийн хөнгөлөлт — 2+ гутал. Кассчин НЭГ дүн бичнэ, гутал бүрд
 * ногдох хэсгийг доор харуулна (баримт дээр мөр бүрийн «Хөнгөлөлт» болно).
 */
function CartDiscount({ value, onChange, lines, shares }: {
  value: string;
  onChange: (v: string) => void;
  lines: CartLine[];
  shares: ReturnType<typeof spreadOrderDiscount>;
}) {
  const given = Number(moneyInput(value)) > 0;
  return (
    <div className="flex flex-col gap-2 rounded-well border border-hair bg-well px-3.5 py-3">
      <Field label="Нийт дүнгийн хөнгөлөлт (₮)">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
          placeholder="0"
          inputMode="numeric"
          className="num h-11 text-right"
        />
      </Field>
      {given && shares ? (
        <ul className="flex flex-col gap-1 text-label text-ink-2">
          {lines.map((l, i) => (
            <li key={l.item.id} className="flex justify-between gap-3">
              <span className="num truncate">{formatSerialDisplay(l.item.serial ?? '')}</span>
              <span className="num">−{formatMnt(shares[i]!.toFixed(2))}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-label text-ink-3">
          {lines.length} гутлын нийт дүнгээс хасна — гутал бүрд үнийн харьцаагаар хуваагдана
        </p>
      )}
    </div>
  );
}

/**
 * Хувь зээлийн хугацаа 1–6 сар — анхдагчгүй, кассчин заавал сонгоно. Сар
 * бүрийн дүн, эхний төлөх өдрийг худалдан авагчид шууд хэлнэ; тооцоо нь
 * серверийнхтэй ИЖИЛ (`installmentSchedule`).
 */
function InstallmentTerm({ total, term, onTerm }: {
  total: number;
  term: number | null;
  onTerm: (months: number) => void;
}) {
  const months = Array.from(
    { length: INSTALLMENT_TERM_MAX - INSTALLMENT_TERM_MIN + 1 },
    (_, i) => INSTALLMENT_TERM_MIN + i,
  );
  const first = term ? installmentSchedule(installmentSplit(total).principalMnt, term, new Date())[0] : null;
  return (
    <Card>
      <CardTitle>Хувь зээлийн хугацаа</CardTitle>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-1.5">
          {months.map((m) => (
            <Chip key={m} active={term === m} onClick={() => onTerm(m)}>
              {m} сар
            </Chip>
          ))}
        </div>
        {first ? (
          <dl className="flex flex-col gap-1 text-sm">
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-ink-2">Сар бүр</dt>
              <dd className="num font-semibold">{formatMnt(first.amountMnt)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-ink-2">1-р төлөлт</dt>
              <dd className="num">{formatDate(first.dueDate)}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-[12.5px] text-ink-3">Хугацааг сонгоно уу — сонгохоос нааш зарагдахгүй.</p>
        )}
      </div>
    </Card>
  );
}

/**
 * Нэмэгдлийн нэг мөр — checkbox + тайлбар + дүн.
 *
 * ⚠️ Мөр бүхэлдээ `<label>` — жижиг checkbox-ыг онож дарах шаардлагагүй
 *    (касс мэдрэгч дэлгэцтэй, ≥44px өндөр).
 */
function SurchargeToggle({
  label, hint, checked, locked = false, onChange, amountMnt,
}: {
  label: string;
  hint: string;
  checked: boolean;
  /** Заавал — кассчин болиулж чадахгүй (зээлийн апп) */
  locked?: boolean;
  onChange: (v: boolean) => void;
  amountMnt: string;
}) {
  return (
    <label
      className={`flex min-h-11 items-center gap-3.5 rounded-well border px-3.5 py-3 transition ${
        locked ? '' : 'cursor-pointer'
      } ${checked ? 'border-accent-300 bg-accent-100' : 'border-hair bg-input hover:bg-row-hover'}`}
    >
      {/* Жинхэнэ checkbox нь дэлгэц уншигч, гарын фокуст — харагдах нь доорх хайрцаг */}
      <input
        type="checkbox"
        checked={checked}
        disabled={locked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className={`grid size-5 shrink-0 place-items-center rounded-md border-[1.5px] text-[13px] font-black
          transition peer-focus-visible:ring-2 peer-focus-visible:ring-accent ${
          checked ? 'pop-in border-accent bg-accent text-bg' : 'border-ink-3'
        }`}
      >
        {checked ? '✓' : ''}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-h3 font-bold">{label}</span>
        <span className="text-label text-ink-3">{hint}</span>
      </span>
      <span className={`ml-auto font-semibold ${checked ? 'text-accent-700' : 'text-ink-3'}`}>
        {checked ? <>+<CountUp text={formatMnt(amountMnt)} duration={600} /></> : '—'}
      </span>
    </label>
  );
}
